import { useEffect } from "react";
import { useRevealChars } from "../../hooks/useRevealChars.ts";
import type { RevealMode } from "../../providers/themeProvider.tsx";
import {
  computeGeometry,
  computeMarks,
  computeTextRegions,
  type TextRegion,
} from "./revealCharsGeometry.ts";
import styles from "./revealCharsLayer.module.scss";

/** CSS-module lookups are `string | undefined` under noUncheckedIndexedAccess.
 *  These classes are authored in the sibling .module.scss; falling back to the
 *  raw name yields an unstyled mark rather than a crash if one is ever renamed. */
const cn = (name: string): string => styles[name] ?? name;

/**
 * Draws what the reveal fonts cannot — see docs/reveal-input-characters.md.
 *
 * The font handles NBSP, C0 controls and every space in All mode. This layer
 * handles the rest:
 *
 *   • both modes — a tick at every zero-width character and soft hyphen. The
 *     browser discards those before font lookup (measured: they lay out at 0px
 *     whatever the cmap says), so no font can ever show them.
 *   • both modes — an arrow over every tab. Chromium 153 lays a tab out as a
 *     blank tab stop and never shapes the font's `→`.
 *   • Edges mode — a band over leading/trailing whitespace, the positional case
 *     a font cannot express. Per line in a textarea, where a trailing space
 *     before a newline is exactly the accident being hunted.
 *   • All mode, textareas — a marker at each hard line break, which draws no
 *     width of its own.
 *
 * Deliberately container-scoped rather than component-scoped: one layer at app
 * root, positioned from getBoundingClientRect(), so it covers all ~530 raw
 * <input>s without a single call-site change or any wrapper element.
 *
 * RTL fields are out of scope and are skipped.
 */

type ActiveRevealMode = Exclude<RevealMode, "off">;
type TargetKind = "input" | "textarea" | "text" | "rich";

/** One text node, or a <br> standing in for a newline, within a rich editor. */
interface RichSeg {
  node: Node;
  isBr: boolean;
  start: number;
  len: number;
}

/**
 * Flatten a contenteditable's contents to the same plain string its owner reads
 * back (text nodes, with a <br> counting as one newline), keeping a map from
 * string offset to DOM node so a region can be turned into a Range.
 */
function collectRich(root: Element): { text: string; segs: RichSeg[] } {
  const segs: RichSeg[] = [];
  let text = "";
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let node: Node | null = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const len = (node as Text).data.length;
      if (len) {
        segs.push({ node, isBr: false, start: text.length, len });
        text += (node as Text).data;
      }
    } else if (node.nodeName === "BR") {
      segs.push({ node, isBr: true, start: text.length, len: 1 });
      text += "\n";
    }
    node = walker.nextNode();
  }
  return { text, segs };
}

/** Range covering [start, end) of the flattened string, across node boundaries. */
function richRange(segs: RichSeg[], start: number, end: number): Range | null {
  const range = document.createRange();
  let started = false;
  for (const seg of segs) {
    const segEnd = seg.start + seg.len;
    if (!started && start >= seg.start && start < segEnd) {
      if (seg.isBr) range.setStartBefore(seg.node);
      else range.setStart(seg.node, start - seg.start);
      started = true;
    }
    if (started && end > seg.start && end <= segEnd) {
      if (seg.isBr) range.setEndAfter(seg.node);
      else range.setEnd(seg.node, end - seg.start);
      return range;
    }
  }
  return null;
}

// Mirrors the exclusions in styles/reveal.css. A field the font does not touch
// must not get marks either.
const EXCLUDED_TYPES = [
  "checkbox", "radio", "range", "color", "file", "image", "submit", "reset",
  "button", "password", "number", "date", "time", "datetime-local", "month", "week",
];
const INPUT_SELECTOR =
  `input${EXCLUDED_TYPES.map(t => `:not([type="${t}"])`).join("")}:not([data-reveal="off"])`;
const TEXTAREA_SELECTOR = `textarea:not([data-reveal="off"])`;
/** Rendered text that is editable but not currently being edited — grid cells.
 *  Opted in with an explicit attribute rather than matched on a hashed CSS
 *  module class name. */
const TEXT_SELECTOR = `[data-reveal-text]:not([data-reveal="off"])`;
/** Rich editors (fwRichTextarea). Multi-line, so gated behind the same opt-in
 *  as <textarea>. Unlike a textarea its text really is in the DOM, so Ranges go
 *  straight onto it — no mirror, and the mixed fonts inside `<fw>` blocks
 *  measure correctly for free. */
const RICH_SELECTOR =
  `[contenteditable="true"]:not([data-reveal="off"]), [contenteditable=""]:not([data-reveal="off"])`;

/** Programmatic `.value` writes fire no event and mutate no attribute, so a
 *  React-driven change is invisible to every observer. Cheap safety net. */
const POLL_MS = 500;

interface Tracked {
  kind: TargetKind;
  clip: HTMLDivElement | null;
  track: HTMLDivElement | null;
  /** Multi-line only: the measurement stand-in. */
  mirror: HTMLDivElement | null;
  marks: HTMLDivElement[];
  /** Cells occupied by the value — zero-width characters excluded. */
  cells: number;
  /** Rebuild key: marks only change when one of these does. */
  lastKey: string;
}

/** Only ever called with a live mode — the component returns early on "off". */
function createLayer(mode: ActiveRevealMode, includeMultiline: boolean): () => void {
  const layer = document.createElement("div");
  layer.className = cn("layer");
  layer.setAttribute("aria-hidden", "true");
  document.body.appendChild(layer);

  const tracked = new Map<Element, Tracked>();
  const visible = new Set<Element>();
  const cellCache = new Map<string, number>();
  const ctx = document.createElement("canvas").getContext("2d");
  const num = (v: string) => parseFloat(v) || 0;

  let frame = 0;
  let needScan = true;
  let destroyed = false;

  function cellWidth(cs: CSSStyleDeclaration): number {
    const font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const cached = cellCache.get(font);
    if (cached !== undefined) return cached;
    let w = 0;
    if (ctx) {
      ctx.font = font;
      w = ctx.measureText("0").width;
    }
    // Canvas refuses some shorthands; fall back to a monospace-ish ratio rather
    // than collapsing every mark onto x=0.
    if (!w || !Number.isFinite(w)) w = (parseFloat(cs.fontSize) || 13) * 0.6;
    cellCache.set(font, w);
    return w;
  }

  /**
   * A modal <dialog> lives in the top layer, which no z-index can reach, so the
   * layer has to move inside it. Everything outside is inert while it is open,
   * so those fields stop being painted too — otherwise their marks would float
   * over the backdrop.
   */
  function modalHost(): HTMLElement | null {
    const dialogs = document.querySelectorAll<HTMLDialogElement>("dialog[open]");
    return dialogs.length ? (dialogs[dialogs.length - 1] ?? null) : null;
  }

  function isPaintable(el: Element, host: HTMLElement | null): boolean {
    if (host && !host.contains(el)) return false;
    if (el.closest('[aria-hidden="true"], [inert]')) return false;
    return true;
  }

  function ensureNodes(t: Tracked): { clip: HTMLDivElement; track: HTMLDivElement } {
    if (!t.clip || !t.track) {
      const clip = document.createElement("div");
      clip.className = cn("clip");
      const track = document.createElement("div");
      track.className = cn("track");
      clip.appendChild(track);
      layer.appendChild(clip);
      t.clip = clip;
      t.track = track;
    }
    return { clip: t.clip, track: t.track };
  }

  function hide(t: Tracked): void {
    if (t.clip) t.clip.style.display = "none";
  }

  function clearMarks(t: Tracked): void {
    for (const m of t.marks) m.remove();
    t.marks = [];
  }

  function valueOf(el: Element, kind: TargetKind): string {
    if (kind === "text") return el.textContent ?? "";
    return (el as HTMLInputElement | HTMLTextAreaElement).value ?? "";
  }

  // ── Single line: <input>, and rendered text in an editable grid cell ───────
  // Both are one line of monospaced text with letter-spacing zeroed, so a
  // character's position is cellIndex * advance.
  function paintSingleLine(el: Element, t: Tracked, rect: DOMRect, cs: CSSStyleDeclaration): void {
    const cell = cellWidth(cs);
    const value = valueOf(el, t.kind);
    const key = `${mode} ${value} ${cell}`;
    const { clip, track } = ensureNodes(t);

    if (key !== t.lastKey) {
      const { marks, cells } = computeMarks(value, cell, mode);
      t.cells = cells;
      t.lastKey = key;
      clearMarks(t);
      t.marks = marks.map(spec => {
        const mark = document.createElement("div");
        mark.className = cn(spec.kind);
        mark.style.left = `${spec.left}px`;
        if (spec.kind !== "tick") mark.style.width = `${spec.width}px`;
        track.appendChild(mark);
        return mark;
      });
    }
    if (!t.marks.length) { hide(t); return; }

    const { clip: box, offsetX } = computeGeometry(
      rect,
      {
        borderLeft: num(cs.borderLeftWidth), borderRight: num(cs.borderRightWidth),
        borderTop: num(cs.borderTopWidth), borderBottom: num(cs.borderBottomWidth),
        paddingLeft: num(cs.paddingLeft), paddingRight: num(cs.paddingRight),
        textAlign: cs.textAlign,
      },
      t.cells, cell, (el as HTMLInputElement).scrollLeft || 0,
    );

    clip.style.display = "";
    clip.style.left = `${box.left}px`;
    clip.style.top = `${box.top}px`;
    clip.style.width = `${box.width}px`;
    clip.style.height = `${box.height}px`;
    track.style.fontSize = cs.fontSize; // tab arrows are sized like the field's text
    track.style.transform = `translateX(${offsetX}px)`;
  }

  /**
   * Textareas cannot use that arithmetic: soft wrapping decides where lines
   * break, and being one line out misplaces everything below it. So a mirror
   * with identical metrics is laid over the field, the browser wraps it, and
   * Range.getClientRects() reads the rectangles back out.
   */
  function paintMultiline(el: HTMLTextAreaElement, t: Tracked, rect: DOMRect, cs: CSSStyleDeclaration): void {
    const bL = num(cs.borderLeftWidth), bR = num(cs.borderRightWidth);
    const bT = num(cs.borderTopWidth), bB = num(cs.borderBottomWidth);
    const { clip, track } = ensureNodes(t);

    let mirror = t.mirror;
    if (!mirror) {
      mirror = document.createElement("div");
      mirror.className = cn("mirror");
      mirror.setAttribute("aria-hidden", "true");
      track.appendChild(mirror);
      t.mirror = mirror;
    }

    const value = el.value ?? "";
    const innerWidth = rect.width - bL - bR;

    // Everything that can change where a character lands.
    const ms = mirror.style;
    ms.width = `${innerWidth}px`;
    ms.boxSizing = "border-box";
    ms.padding = `${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft}`;
    ms.fontFamily = cs.fontFamily;
    ms.fontSize = cs.fontSize;
    ms.fontWeight = cs.fontWeight;
    ms.fontStyle = cs.fontStyle;
    ms.letterSpacing = cs.letterSpacing;
    ms.lineHeight = cs.lineHeight;
    ms.textAlign = cs.textAlign;
    ms.tabSize = cs.tabSize;
    ms.overflowWrap = cs.overflowWrap;
    ms.wordBreak = cs.wordBreak;
    ms.whiteSpace = cs.whiteSpace === "pre" ? "pre" : "pre-wrap";

    // Position and reveal the clip BEFORE measuring. rebuildMultiline reads the
    // mirror's layout, and a display:none clip has none — every rect comes back
    // zero and the marks land in the corner. Because the rebuild only re-runs
    // when the value changes, they then stay wrong until the next keystroke,
    // which is exactly how this showed up: a line-end marker that only appeared
    // once you typed on the new line.
    clip.style.display = "";
    clip.style.left = `${rect.left + bL}px`;
    clip.style.top = `${rect.top + bT}px`;
    clip.style.width = `${innerWidth}px`;
    clip.style.height = `${rect.height - bT - bB}px`;

    const key = `${mode} ${value} ${innerWidth} ${cs.fontSize} ${cs.fontFamily}`;
    if (key !== t.lastKey || mirror.textContent !== value) {
      mirror.textContent = value;
      t.lastKey = key;
      rebuildMultiline(t, track, mirror, computeTextRegions(value, mode));
    }

    // The mirror rides the field's scroll; marks sit inside it, so they follow.
    track.style.fontSize = cs.fontSize;
    track.style.transform = `translate(${-el.scrollLeft}px, ${-el.scrollTop}px)`;

    if (!t.marks.length) hide(t);
  }

  function rebuildMultiline(
    t: Tracked, track: HTMLDivElement, mirror: HTMLDivElement, regions: TextRegion[],
  ): void {
    clearMarks(t);
    const node = mirror.firstChild;
    if (!node) return;

    const origin = mirror.getBoundingClientRect();
    const range = document.createRange();

    for (const region of regions) {
      try {
        range.setStart(node, region.start);
        range.setEnd(node, region.end);
      } catch {
        continue; // offsets can go stale between a value change and this paint
      }
      // Every region is a real character range, so getClientRects() reports a
      // box per line box it spans. A tick's zero-width character and a
      // lineEnd's newline legitimately measure 0 wide — only a *band* with no
      // width means the range found nothing worth drawing.
      const zeroWidthKind = region.kind === "tick" || region.kind === "lineEnd";
      let rects: DOMRect[] = [...range.getClientRects()];
      if (!rects.length) rects = [range.getBoundingClientRect()];

      for (const r of rects) {
        if (r.width === 0 && !zeroWidthKind) continue;
        if (r.width === 0 && r.height === 0) continue; // degenerate, no position
        const mark = document.createElement("div");
        mark.className = cn(region.kind);
        mark.style.left = `${r.left - origin.left}px`;
        mark.style.top = region.kind === "lineEnd"
          ? `${r.bottom - origin.top - 3}px`
          : `${r.top - origin.top}px`;
        if (region.kind === "band" || region.kind === "tab") {
          mark.style.width = `${r.width}px`;
          mark.style.height = `${r.height}px`;
        } else if (region.kind === "tick") {
          mark.style.height = `${r.height}px`;
        }
        track.appendChild(mark);
        t.marks.push(mark);
      }
    }
  }

  /**
   * Rich editors are measured live rather than through a mirror, so the rects
   * move with the element's own scroll and must be recomputed each paint, not
   * only when the value changes. Marks are reused in place when the count is
   * unchanged so a scroll does not churn the DOM every frame.
   */
  function paintRich(el: HTMLElement, t: Tracked, rect: DOMRect, cs: CSSStyleDeclaration): void {
    const bL = num(cs.borderLeftWidth), bR = num(cs.borderRightWidth);
    const bT = num(cs.borderTopWidth), bB = num(cs.borderBottomWidth);
    const { clip, track } = ensureNodes(t);

    clip.style.display = "";
    clip.style.left = `${rect.left + bL}px`;
    clip.style.top = `${rect.top + bT}px`;
    clip.style.width = `${rect.width - bL - bR}px`;
    clip.style.height = `${rect.height - bT - bB}px`;
    track.style.transform = "";
    track.style.fontSize = cs.fontSize;

    const { text, segs } = collectRich(el);
    const originLeft = rect.left + bL;
    const originTop = rect.top + bT;

    interface Placed { kind: string; left: number; top: number; width: number; height: number }
    const placed: Placed[] = [];
    for (const region of computeTextRegions(text, mode)) {
      const range = richRange(segs, region.start, region.end);
      if (!range) continue;
      let rects: DOMRect[] = [...range.getClientRects()];
      if (!rects.length) rects = [range.getBoundingClientRect()];
      for (const r of rects) {
        if (r.width === 0 && (region.kind === "band" || region.kind === "tab")) continue;
        if (r.width === 0 && r.height === 0) continue;
        placed.push({
          kind: region.kind,
          left: r.left - originLeft,
          top: region.kind === "lineEnd" ? r.bottom - originTop - 3 : r.top - originTop,
          width: r.width,
          height: r.height,
        });
      }
    }

    if (!placed.length) { clearMarks(t); hide(t); return; }

    if (t.marks.length !== placed.length) {
      clearMarks(t);
      t.marks = placed.map(() => {
        const mark = document.createElement("div");
        track.appendChild(mark);
        return mark;
      });
    }
    placed.forEach((p, i) => {
      const mark = t.marks[i];
      if (!mark) return;
      mark.className = cn(p.kind);
      mark.style.left = `${p.left}px`;
      mark.style.top = `${p.top}px`;
      if (p.kind === "band" || p.kind === "tab") {
        mark.style.width = `${p.width}px`;
        mark.style.height = `${p.height}px`;
      } else if (p.kind === "tick") {
        mark.style.width = "";
        mark.style.height = `${p.height}px`;
      } else {
        mark.style.width = "";
        mark.style.height = "";
      }
    });
  }

  function scan(): void {
    const seen = new Set<Element>();
    const add = (el: Element, kind: TargetKind) => {
      seen.add(el);
      if (tracked.has(el)) return;
      tracked.set(el, { kind, clip: null, track: null, mirror: null, marks: [], cells: 0, lastKey: "" });
      io.observe(el);
    };

    for (const el of document.querySelectorAll(INPUT_SELECTOR)) add(el, "input");
    if (includeMultiline) {
      for (const el of document.querySelectorAll(TEXTAREA_SELECTOR)) add(el, "textarea");
      for (const el of document.querySelectorAll(RICH_SELECTOR)) add(el, "rich");
    }
    for (const el of document.querySelectorAll(TEXT_SELECTOR)) {
      // Arithmetic assumes one plain text node; a decorated cell (chips, links,
      // icons) has element children and is skipped rather than mismeasured.
      if (el.childElementCount === 0) add(el, "text");
    }

    for (const el of [...tracked.keys()]) {
      if (seen.has(el)) continue;
      io.unobserve(el);
      tracked.get(el)?.clip?.remove();
      tracked.delete(el);
      visible.delete(el);
    }
  }

  function paint(): void {
    if (destroyed) return;
    const host = modalHost();
    const desiredParent = host ?? document.body;
    if (layer.parentElement !== desiredParent) desiredParent.appendChild(layer);

    for (const [el, t] of tracked) {
      if (!visible.has(el) || !isPaintable(el, host)) { hide(t); continue; }

      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) { hide(t); continue; }

      const cs = getComputedStyle(el);
      if (cs.direction === "rtl") { hide(t); continue; }

      if (t.kind === "textarea") paintMultiline(el as HTMLTextAreaElement, t, rect, cs);
      else if (t.kind === "rich") paintRich(el as HTMLElement, t, rect, cs);
      else paintSingleLine(el, t, rect, cs);
    }
  }

  function schedule(): void {
    if (frame || destroyed) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (needScan) { needScan = false; scan(); }
      paint();
    });
  }

  const io = new IntersectionObserver(entries => {
    for (const e of entries) {
      const el = e.target;
      if (e.isIntersecting) visible.add(el);
      else {
        visible.delete(el);
        const t = tracked.get(el);
        if (t) hide(t);
      }
    }
    schedule();
  });

  // A rescan runs querySelectorAll over the whole document, so it is reserved for
  // mutations that can change WHICH fields exist. Watching class/style here would
  // fire on every hover and selection change in a grid and rescan each frame —
  // those only ever move a field, which the next scroll/input/poll repaint picks
  // up anyway.
  const RESCAN_ATTRS = new Set(["type", "data-reveal", "data-reveal-text", "contenteditable"]);
  const mo = new MutationObserver(records => {
    for (const r of records) {
      if (r.type === "childList" || (r.attributeName && RESCAN_ATTRS.has(r.attributeName))) {
        needScan = true;
        break;
      }
    }
    schedule();
  });
  mo.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["open", "type", "data-reveal", "data-reveal-text", "contenteditable", "aria-hidden", "inert"],
  });

  const onAny = () => schedule();
  // Capture phase: inner scrollers (grid viewport, modal body) do not bubble scroll.
  document.addEventListener("scroll", onAny, true);
  document.addEventListener("input", onAny, true);
  document.addEventListener("keyup", onAny, true);
  document.addEventListener("mouseup", onAny, true);
  document.addEventListener("focusin", onAny, true);
  document.addEventListener("focusout", onAny, true);
  window.addEventListener("resize", onAny);
  const poll = window.setInterval(onAny, POLL_MS);

  schedule();

  return () => {
    destroyed = true;
    if (frame) cancelAnimationFrame(frame);
    window.clearInterval(poll);
    io.disconnect();
    mo.disconnect();
    document.removeEventListener("scroll", onAny, true);
    document.removeEventListener("input", onAny, true);
    document.removeEventListener("keyup", onAny, true);
    document.removeEventListener("mouseup", onAny, true);
    document.removeEventListener("focusin", onAny, true);
    document.removeEventListener("focusout", onAny, true);
    window.removeEventListener("resize", onAny);
    layer.remove();
    tracked.clear();
    visible.clear();
  };
}

export function RevealCharsLayer() {
  const { revealMode, revealTextarea } = useRevealChars();

  useEffect(() => {
    if (revealMode === "off") return;
    return createLayer(revealMode, revealTextarea);
  }, [revealMode, revealTextarea]);

  return null;
}
